<script>
  import { onMount } from "svelte";
  import { getAccount, account } from "$lib/stores/state.svelte.js";
  import { loadConfig } from "$lib/utils/helpers.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
  import CircleUserRound from "@lucide/svelte/icons/circle-user-round";
  import AccountDropdownContent from "$app/AccountDropdownContent.svelte";
  import Head from "$app/Head.svelte";

  // Section components
  import FeaturesSection from "$app/FeaturesSection.svelte";
  import HowItWorksSection from "$app/HowItWorksSection.svelte";
  import CodeExampleSection from "$app/CodeExampleSection.svelte";
  import Player from "$app/Player.svelte";
  import UseCasesSection from "$app/UseCasesSection.svelte";
  import PricingContent from "$app/PricingContent.svelte";
  import FAQSection from "$app/FAQSection.svelte";
  import FinalCTASection from "$app/FinalCTASection.svelte";
  import Footer from "$app/Footer.svelte";

  // Import song files as raw text (embedded at build time)
  import retroSynthwaveDrive from "../public/songs/retro_synthwave_drive.song?raw";
  import pianoBattle from "../public/songs/piano_battle.song?raw";
  import lofiStudy from "../public/songs/lofi_synth_studying.song?raw";

  const demoSongs = [
    { name: "Retro Synthwave Drive", content: retroSynthwaveDrive },
    { name: "Piano Battle", content: pianoBattle },
    { name: "Lo-Fi Study", content: lofiStudy },
  ];

  const config = loadConfig();

  // Props with default content
  const features = {
    title: "Your song, rendered in seconds",
    subtitle:
      "Everything you need to go from idea to finished song",
    features: [
      {
        title: "Skip the Learning Curve",
        description:
          "Compose using a simple text-based format. No complex DAW required. Just type your music using scales, chords, and patterns.",
      },
      {
        title: "AI Does the Heavy Lifting",
        description:
          "Ask the AI to generate sections, tracks, or write entire arrangements. The AI understands the text-based song format, music composition, and music theory.",
      },
      {
        title: "Wide Range of Instruments",
        description:
          "You can use 100+ synth instruments, 20+ sampled instruments, and 2 full MIDI soundfonts. Each instrument can be customized with a wide variety of properties, such as filters and effects."
      },
      {
        title: "Iterate Quickly",
        description:
          "Change a chord or scale, adjust the tempo, swap instruments. See your changes and hear them after a quick render.",
      },
      {
        title: "Export Your Music",
        description:
          "Render your song files as MP3 files and download them.",
      },
      {
        title: "Own Your Work",
        description:
          "Your music is yours to use however you want. In a video, in a game, on the radio—it's up to you!",
      },
    ],
  };
  const howItWorks = {
    title: "Create your first song in under 5 minutes",
    subtitle: "Just type and play.",
    steps: [
      {
        number: "01",
        title: "Write your Song File",
        description:
          "Learn the song file format and write it by hand, or ask the AI to write a song for you.",
      },
      {
        number: "02",
        title: "Press Play",
        description:
          "Your song renders in seconds with realistic instruments. Adjust anything and render again.",
      },
      {
        number: "03",
        title: "Export and Share",
        description:
          "When you're done editing, download your song as an MP3 and share it with the world.",
      },
    ],
  };
  const codeExample = {
    title: "This is all it takes",
    subtitle:
      "A simple text format that puts composition first. No complicated software required.",
    filename: "retro-synthwave-drive.toml",
    code: retroSynthwaveDrive,
  };
  const useCases = {
    title: "Perfect for every creator",
    subtitle:
      "Whether you're a professional or just getting started, SongFormat adapts to your workflow",
    useCases: [
      {
        title: "Content Creators",
        description:
          "Generate custom background music for YouTube videos, podcasts, and streams without worrying about copyright strikes.",
        benefits: [
          "Royalty-free music you own",
          "Match mood to content perfectly",
          "Export MP3 ready to use",
        ],
      },
      {
        title: "Indie Musicians",
        description:
          "Sketch out song ideas, experiment with arrangements, and create full demos without expensive studio time.",
        benefits: [
          "100+ instruments included",
          "Quick iteration on ideas",
          "Text format for version control",
        ],
      },
      {
        title: "Hobbyists & Learners",
        description:
          "Explore music composition without years of theory. The AI can help you understand chords, scales, and arrangement structure.",
        benefits: [
          "No music theory required",
          "AI teaches as you create",
          "Free to experiment manually",
        ],
      },
    ],
  };
  const faq = {
    title: "Frequently asked questions",
    subtitle: "Here's what you need to know",
    faqs: [
      {
        question: "How many credits does it cost to render a song?",
        answer:
          "When you render your song from the text-based song format to audio, it costs 1 credit for every 30 minutes rendered.",
      },
      {
        question: "How many credits does it cost to have AI create a song?",
        answer:
          "When you use the AI, you pay per AI token. Input tokens cost 1 credit per million tokens, and output tokens cost 5 credits per million tokens. All of the demo songs on this page cost less than 2 credits to generate.",
      },
      {
        question: "Do I need any musical training?",
        answer:
          "No. If you can describe what you want, the AI can create it, even if it's a short basic description like \"A 30 second jazzy intro for a podcast.\" Of course, more detail will lead to better results. And if you can mix in some knowledge of music theory and composition, it can be helpful. But you can also ask the AI to help you.",
      },
      {
        question: "What can I do with the music I create?",
        answer:
          "Whatever you want. It's your music. Use it in videos, release it to the world, or sell it.",
      },
      {
        question: "What do I get when I export?",
        answer:
          "You can export your songs as MP3s or the text-based song format to edit anywhere. Your music is always yours.",
      },
    ],
  };
  const finalCTA = {
    title: "Your next song is waiting",
    highlightWord: "waiting",
    subtitle:
      "Stop wrestling with complicated software. Start creating music today.",
    ctaButton: {
      text: "Get started",
      href: "/pricing",
    },
    footerText: null,
  };

  onMount(async () => {
    await getAccount();
  });
</script>

<Head title={config.app.description} />

<div class="min-h-screen bg-[#0a0a0f] text-[#c8c8cc] font-mono">
  <!-- Terminal title bar (header) -->
  <div class="fixed top-0 left-0 right-0 z-[70] bg-[#0e0e16]/95 backdrop-blur-sm border-b-2 border-[#3a3a4a]">
    <div class="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between">
      <a href="/" class="flex items-center gap-2 group">
        <img src="/images/logo.webp" class="size-5 opacity-80 group-hover:opacity-100 transition-opacity" alt="Logo" />
        <span class="font-bold text-sm text-[#e8e8ec] tracking-wider">
          {config.app.name}
        </span>

      </a>
      <div class="flex items-center gap-2">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            {#snippet child({ props })}
              <Button
                variant="ghost"
                size="icon"
                class="text-[#a0a0a8] hover:text-[#e8e8ec] hover:bg-[#16161f]"
                {...props}
              >
                <CircleUserRound class="size-4" />
              </Button>
            {/snippet}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            class="w-(--bits-dropdown-menu-anchor-width) min-w-56 rounded-lg bg-[#101018] border border-[#3a3a4a] text-[#c8c8cc]"
            align="end"
            sideOffset={4}
          >
            <AccountDropdownContent />
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  </div>

  <!-- Hero / Demos -->
  <section class="border-b border-[#3a3a4a]">
    <div class="container mx-auto px-4 max-w-6xl py-16 md:py-24">
      <div class="text-center mb-10 flex flex-col gap-2">
        <div class="flex items-center justify-center gap-3">
          <img src="/images/logo.webp" class="size-16" alt="Logo" />
          <h1 class="text-3xl md:text-5xl font-bold text-[#e8e8ec] tracking-tight">
            {config.app.name}
          </h1>
        </div>
        <div class="text-base md:text-lg text-[#a0a0a8] max-w-xl mx-auto leading-relaxed flex flex-col gap-0">
          <div>a text-based song format</div>
          <div>for generating music,</div>
          <div>with help from AI</div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {#each demoSongs as song}
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2 px-3 py-2 bg-[#101018] border border-[#3a3a4a]">
              <span class="text-[#7ec699] text-xs">▶</span>
              <h3 class="text-sm text-[#b0b0b8]">{song.name}</h3>
            </div>
            <div class="h-[300px] w-full border border-[#3a3a4a] bg-[#101018] p-2">
              <Player songFile={song.content} mode="visual" />
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <main class="w-full flex flex-col gap-0">
    <FeaturesSection {...features} />
    <HowItWorksSection {...howItWorks} />
    <CodeExampleSection {...codeExample} />
    <UseCasesSection {...useCases} />
    <PricingContent variant="section" />
    <FAQSection {...faq} />
    <FinalCTASection {...finalCTA} />
  </main>

  <Footer />
</div>
